import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  dismissAssertionReport,
  reportAssertion,
  type Assertion,
  type ReportReason
} from '@peruselab/api-client/assertions';
import { usePublicAccess } from '../../public/publicAccess';
import { Icon } from '../../../components/Icon';

const reasons: readonly ReportReason[] = ['WRONG_VALUE', 'WRONG_UNIT', 'WRONG_COMPOUND', 'OTHER'];

export function AssertionProblemReport({
  assertion,
  onChanged
}: {
  readonly assertion: Assertion;
  readonly onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { signedIn } = usePublicAccess();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason>('WRONG_VALUE');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const report = assertion.problemReport;
  const submit = async (dismiss = false) => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      if (dismiss && report) await dismissAssertionReport(assertion.id, report.id);
      else await reportAssertion(assertion.id, { reason, note: note.trim() });
      await onChanged();
      setOpen(false);
      setNote('');
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  if (report)
    return (
      <div className="assertion-problem-report">
        <span className="comment-badge">{t('properties.report.badge')}</span>
        <p>
          {t(`properties.report.reasons.${report.reason}`)}: <span dir="auto">{report.note}</span>
        </p>
        <p>{t('properties.report.excluded')}</p>
        {signedIn && report.canDismiss ? (
          <button
            type="button"
            className="icon-button"
            disabled={busy}
            onClick={() => void submit(true)}
            aria-label={t('properties.report.dismiss')}
            title={t('properties.report.dismiss')}
          >
            <Icon name="close" />
          </button>
        ) : null}
        {error ? <p role="alert">{t('properties.report.failed')}</p> : null}
      </div>
    );
  if (!signedIn || !assertion.imported || assertion.withdrawn || assertion.supersededBy) return null;
  return (
    <div className="assertion-problem-report">
      {!open ? (
        <button
          type="button"
          className="icon-button"
          onClick={() => setOpen(true)}
          aria-label={t('properties.report.action')}
          title={t('properties.report.action')}
        >
          <Icon name="flag" />
        </button>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <p>{t('properties.report.hint')}</p>
          <label htmlFor={`${id}-reason`}>{t('properties.report.reason')}</label>
          <select
            id={`${id}-reason`}
            value={reason}
            disabled={busy}
            onChange={(event) => setReason(event.target.value as ReportReason)}
          >
            {reasons.map((value) => (
              <option key={value} value={value}>
                {t(`properties.report.reasons.${value}`)}
              </option>
            ))}
          </select>
          <label htmlFor={`${id}-note`}>{t('properties.report.note')}</label>
          <textarea
            id={`${id}-note`}
            value={note}
            maxLength={1000}
            required
            disabled={busy}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="assertion-actions">
            <button
              type="submit"
              disabled={busy || !note.trim()}
              className="icon-button"
              aria-label={t('properties.report.submit')}
              title={t('properties.report.submit')}
            >
              <Icon name="send" />
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setError(false);
              }}
              className="icon-button"
              aria-label={t('properties.report.cancel')}
              title={t('properties.report.cancel')}
            >
              <Icon name="close" />
            </button>
          </div>
          {error ? <p role="alert">{t('properties.report.failed')}</p> : null}
        </form>
      )}
    </div>
  );
}
